import { AbsoluteResolvedModelPath, createAbsolute } from "../../../../Common/ModelPath/ModelPath";
import { DraftFullId } from "../../../../Common/CandyApiClient/Types/DraftFullId";
import type { AuthenticationMode } from "../../../../Common/HttpClient/AuthenticationMode";
import { FormInfo } from "../../../../Common/CandyApiClient/ClientDraftInfo/FnsFormInfo";
import { PrintInfo } from "../../../../Common/CandyApiClient/ClientDraftInfo/PrintInfo";
import { GFV } from "../../../../Common/GFV";
import type { LayoutEventsStatic } from "../../Controls/Internals/LayoutEvents/LayoutEvents";
import { InvalidProgramStateError } from "../../../../Common/Errors";
import { customButtonOptions } from "../../Controls/FormParts/SimpleForm/SimpleForm";
import { ActionDescription } from "../Client/Client";
import { ExtraPageAction, SetUnitItemExtraInfo } from "../../Controls/FormParts/Unit/Types";
import { ThemeVariant } from "../../Controls/Internals/ThemeProvider/ThemeProvider";

import { AUOOptions } from "./AUOOptions";
import type { IKeWebTemplatesApiClient } from "./IKeWebTemplatesApiClient";

export interface FormSettings extends Partial<AUOOptions> {
    draftId: string;
    ns: string;
    gfv: GFV;
    authentication?: AuthenticationMode;
    setModalContext?: boolean;
    doNotValidateFormOnStart?: boolean;
    disableMinitours?: boolean;
    resourceHash?: string;
    employerId?: string;
    navigationQueryPath?: string;
    navigationPath?: string;
    navigationAttachmentId?: string;
    navigationHighlanderPath?: string;
    attachmentId?: string;
    isIndividual?: boolean;
    isForeign?: boolean;
    canCheckByPopd?: boolean;
    isTestServer?: boolean;
    renderCachingForIE8?: boolean;
    normalizationOnServer?: boolean;
    debug?: boolean;
    printFromEditor?: boolean;
    noLoader?: boolean;
    formRoot: string;
    // Work only with SimpleForm container
    showFormContainer: boolean;
    hasTemplates?: boolean;
    embeddedLogEnabled: boolean;
    theme?: ThemeVariant;
    enableCustomizationButton?: boolean;
    allowsend: boolean;
    printInfo: PrintInfo;
    regions: string[];
    hideFufDownloadButton?: boolean;
    feedbackEnabled?: boolean;
    isDisabledCloseButton?: boolean;
    isDisabledCloseAndSaveButton?: boolean;
    isDisabledCleanForm?: boolean;
    isINNEnabled?: boolean;
    prevGfvDraftId?: string;
    model?: { [path: string]: unknown };
    isDisabledTour?: boolean;
    shownTours?: string[];
    resourceZone?: string;
    lang?: string;
    isReadOnly?: boolean;
    enableAutocalcsInReadOnly?: boolean;
    engineVersion?: string;
    externalInitSectionPath?: string;
    interdocumentTransfer?: boolean;
    templatesApi?: IKeWebTemplatesApiClient;
    panelActions?: ActionDescription[];
    hidePanelActions?: boolean;
    showUnitBorder?: boolean;
    showNavigationSidebar?: boolean;
    ieDeprecationWarningEnabled?: boolean;
    formInfo?: FormInfo;
    externalOnClickByPathWithSingleCallback?: onClickByPathWithSingleCallback;
    eventEmitter?: LayoutEventsStatic;
    __onlyForTests__enableLegacyValidators?: boolean;
    keformsUrl: string;
    normativeUrl?: string;
    staffWidgetUrl?: string;
    contractorsWidgetUrl?: string;
    staffServiceUrl: string;
    contractorsApiUrl: string;
    picklistUrl: string;
    nidusUrl: string;
    fsPrinterUrl: string;
    ndsPlusApiUrl?: string;
    portalUserId?: string | null;
    useHeavyErrorLog?: boolean;
    isFormInFormNds?: boolean;
    useExtendedErrorLog: boolean;
    useServerSideFLangNormalization?: boolean;
    useServerSideCalculations?: boolean;
    disableFlangRulesForEnableAutoFieldFeature?: boolean;
    hideFormLoader?: boolean;
    restoreToSourceFuf?: boolean;

    /**
     * Используется для тестирования (на момент добавления в Диадоке)
     */
    passBindingPathsIntoDomElements?: boolean;

    onRemoveAttachment?: () => void;
    onClearAttachment?: () => void;
    attachmentPageActions?: ExtraPageAction[];
    setUnitItemExtraInfo?: SetUnitItemExtraInfo;
    attachmentUnitItemInfoPaths?: AbsoluteResolvedModelPath[];

    //--- Доступность нулевой отчетности для этой формы
    zeroReportingEnabled?: boolean;
    // Если пользователь выбрал создать нулевой отчет в ЛБ создания, то мы показываем поляху с настроками
    callInitZeroReport?: boolean;
    //---

    /**
     * сделано для фсс кастомной кнопки https://kontur.slack.com/archives/C08EAL0P2/p1686725230032659?thread_ts=1685968468.539989&cid=C08EAL0P2
     Выпилить в рамках https://yt.skbkontur.ru/issue/FS-7999/Pereehat-na-generaciyu-kontenta-futera-v-SimpleForm-iz-resursov
     * */
    sendToSignButton?: customButtonOptions;
    signAndSendButton?: customButtonOptions;
    // --- Виджет истории редактирования
    innerHistoryEnabled?: boolean;
    isNeedNdsRevise?: boolean;
    showErrorsCountInPanel?: boolean;
}

export interface onClickByPathWithSingleCallback {
    pathMasks: string[];
    callback: (resolvedPath: string) => Promise<void>;
}

export function getDraftFullId(settings: FormSettings): DraftFullId {
    return {
        draftId: settings.draftId,
        ns: settings.ns,
    };
}

export function getFormRootModelPath(settings: FormSettings): AbsoluteResolvedModelPath {
    const iModelPath = createAbsolute(settings.formRoot);
    if (!iModelPath.isResolved()) {
        throw new InvalidProgramStateError();
    }
    return iModelPath;
}

export const autocalcsDisabledInReadOnly = (settings: FormSettings) =>
    settings.useServerSideFLangNormalization === true &&
    settings.isReadOnly === true &&
    !settings.enableAutocalcsInReadOnly;
